import type {
	BankAccountIdentifierEntryDto,
	BankAccountRes,
	CurrencyType,
} from '@dealstack/shared/lib/generated';

// The bank account fields the shared form and tile read. A person's account arrives as
// BankAccountRes and a programme's as DataBankAccountTct; both satisfy this subset, so
// neither component has to know which endpoint produced the account.
export type BankAccountDetails = Pick<
	BankAccountRes,
	| 'id'
	| 'accountHolder'
	| 'bankName'
	| 'accountNumber'
	| 'iban'
	| 'swiftCode'
	| 'countryCode'
	| 'currency'
	| 'identifiers'
> &
	// Optional so legacy hosts without lifecycle data still satisfy the shape.
	Partial<Pick<BankAccountRes, 'status'>>;

export enum BANK_ACCOUNT_FORM_FIELDS {
	account_number = 'accountNumber',
	bank_name = 'bankName',
	bic = 'bic',
	iban = 'iban',
	country_code = 'countryCode',
	verified_country_code = 'verifiedCountryCode',
	currency = 'currency',
	account_name = 'accountName',
	format_rule_id = 'formatRuleId',
	identifiers = 'identifiers',
}

export interface BankAccountFormValues {
	[BANK_ACCOUNT_FORM_FIELDS.iban]: string;
	[BANK_ACCOUNT_FORM_FIELDS.bank_name]?: string;
	[BANK_ACCOUNT_FORM_FIELDS.country_code]?: string | null;
	// Country derived by a successful IBAN lookup — submitted to the BE in place of
	// the selected country, never shown in the UI.
	[BANK_ACCOUNT_FORM_FIELDS.verified_country_code]?: string | null;
	[BANK_ACCOUNT_FORM_FIELDS.currency]?: CurrencyType | null;
	[BANK_ACCOUNT_FORM_FIELDS.account_number]: string;
	[BANK_ACCOUNT_FORM_FIELDS.bic]: string;
	[BANK_ACCOUNT_FORM_FIELDS.account_name]: string;
	// The selected entry format, held by id: names are display copy and get renamed
	// (MIP-2543 renamed GB's "Sort code and account number" to "Local format").
	[BANK_ACCOUNT_FORM_FIELDS.format_rule_id]?: string | null;
	[BANK_ACCOUNT_FORM_FIELDS.identifiers]?: Record<string, string>;
}

export interface BankAccountSubmitPayload {
	accountHolder: string;
	bankName?: string;
	accountNumber?: string;
	swiftCode?: string;
	iban?: string;
	countryCode: string | null;
	currency: CurrencyType;
	formatRuleId?: string;
	identifiers?: BankAccountIdentifierEntryDto[];
}

export interface BankAccountCountryOption {
	value: string;
	label: string;
}
